import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Siren, Sparkles, Trash2, CheckCircle2, Eye, AlertTriangle, RefreshCw } from "lucide-react";

const SEVERITY_COLOR = {
  high: "bg-[#fff0f5] text-[#c01974]",
  medium: "bg-[#fff6e6] text-[#b05f00]",
  low: "bg-[#eaf8de] text-[#3f7c21]",
};
const STATUS_COLOR = {
  open: "bg-[#fff0f5] text-[#c01974]",
  ack: "bg-[#fff6e6] text-[#b05f00]",
  resolved: "bg-[#eaf8de] text-[#3f7c21]",
};

/**
 * AI Incident Feed — /admin/incidents
 *
 * Backend middleware + frontend reporter capture every exception + 5xx +
 * uncaught UI error and cluster them by (error_type, stack_top, path).
 * Clicking "Analyze" calls Anthropic Claude (server `LLM_MODEL`) to produce a
 * root-cause + suggested fix for the cluster — read-only, human-in-the-loop.
 */
export default function Incidents() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ open: 0, ack: 0, resolved: 0, total: 0 });
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [emitting, setEmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/incidents${filter ? `?status=${filter}` : ""}`);
      setItems(data.incidents);
      setTotals(data.totals);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (iid, status) => {
    await api.put(`/incidents/${iid}/status`, { status });
    if (opened?.id === iid) setOpened({ ...opened, status });
    await load();
  };

  const remove = async (iid) => {
    if (!window.confirm("Delete this incident cluster?")) return;
    await api.delete(`/incidents/${iid}`);
    if (opened?.id === iid) setOpened(null);
    await load();
  };

  const analyze = async (iid) => {
    setAnalyzing(iid);
    try {
      const { data } = await api.post(`/incidents/${iid}/analyze`);
      if (opened?.id === iid) setOpened(data);
      await load();
    } catch (e) {
      window.alert("AI analysis failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setAnalyzing(null);
    }
  };

  const emitTest = async () => {
    setEmitting(true);
    try {
      await api.post("/incidents/_test/emit");
    } catch {
      // expected — the endpoint throws by design
    }
    setEmitting(false);
    await load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2" data-testid="incidents-title">
            <Siren className="h-6 w-6 text-[#c01974]" /> AI Incident Feed
          </h1>
          <p className="text-sm text-[#5c665a]">
            Unhandled exceptions, HTTP 5xx and frontend crashes, clustered by root cause. Click <strong>Analyze</strong> to get a plain-English RCA + suggested fix from Claude (see server <code className="text-[11px]">LLM_MODEL</code>).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="admin-btn-ghost" onClick={load} data-testid="incidents-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button className="admin-btn-ghost text-[#c01974]" onClick={emitTest} disabled={emitting} data-testid="incidents-emit-test">
            {emitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
            Emit test incident
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="incidents-filter-bar">
        {[
          { k: "open", label: "Open", n: totals.open },
          { k: "ack", label: "Acknowledged", n: totals.ack },
          { k: "resolved", label: "Resolved", n: totals.resolved },
          { k: "", label: `All (${totals.total})`, n: null },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${
              filter === f.k ? "bg-[#1a1c19] text-white" : "bg-white text-[#5c665a] border border-[#e2e8e2]"
            }`}
            data-testid={`incidents-filter-${f.k || "all"}`}
          >
            {f.label}{f.n !== null ? ` · ${f.n}` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#66cc33]" />
        </div>
      ) : items.length === 0 ? (
        <div className="admin-card text-center py-12">
          <CheckCircle2 className="mx-auto h-8 w-8 text-[#66cc33]" />
          <div className="mt-2 font-semibold text-[#1a1c19]">No incidents in this bucket</div>
          <div className="text-xs text-[#5c665a]">
            The middleware will log any unhandled exception or 5xx here automatically.
          </div>
        </div>
      ) : (
        <div className="admin-card overflow-x-auto p-0" data-testid="incidents-table-wrapper">
          <table className="min-w-full text-sm">
            <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
              <tr>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Error</th>
                <th className="px-4 py-3">Path</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-right">Count</th>
                <th className="px-4 py-3">Last seen</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8e2]">
              {items.map((inc) => (
                <tr key={inc.id} data-testid={`incident-row-${inc.id}`} className="hover:bg-[#fafcfa]">
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${SEVERITY_COLOR[inc.severity] || SEVERITY_COLOR.medium}`}>
                      {inc.severity}
                    </span>
                  </td>
                  <td className="px-4 py-2 max-w-xs">
                    <div className="truncate font-mono text-xs font-bold text-[#1a1c19]" title={inc.error_type}>{inc.error_type}</div>
                    <div className="truncate text-[11px] text-[#5c665a]" title={inc.message}>{inc.message}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-[11px] text-[#5c665a]">{inc.method ? `${inc.method} ` : ""}{inc.path || "—"}</td>
                  <td className="px-4 py-2 text-xs">{inc.source}</td>
                  <td className="px-4 py-2 text-right font-bold">{inc.count}</td>
                  <td className="px-4 py-2 text-xs text-[#5c665a]">{inc.last_seen?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLOR[inc.status] || STATUS_COLOR.open}`}>
                      {inc.status}
                    </span>
                    {inc.ai && <span className="ml-1 rounded-full bg-[#eef2ff] px-2 py-0.5 text-[9px] font-bold text-[#4c51bf]">AI</span>}
                  </td>
                  <td className="px-4 py-2 space-x-1 whitespace-nowrap">
                    <button className="admin-btn-ghost" onClick={() => setOpened(inc)} data-testid={`incident-view-${inc.id}`}>
                      <Eye className="h-3.5 w-3.5" /> View
                    </button>
                    <button className="admin-btn" onClick={() => analyze(inc.id)} disabled={analyzing === inc.id} data-testid={`incident-analyze-${inc.id}`}>
                      {analyzing === inc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      {analyzing === inc.id ? "Analyzing…" : inc.ai ? "Re-analyze" : "Analyze"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {opened && (
        <IncidentDrawer
          inc={opened}
          onClose={() => setOpened(null)}
          onStatus={setStatus}
          onDelete={remove}
          onAnalyze={analyze}
          analyzing={analyzing === opened.id}
        />
      )}
    </div>
  );
}

function IncidentDrawer({ inc, onClose, onStatus, onDelete, onAnalyze, analyzing }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="incident-drawer">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${SEVERITY_COLOR[inc.severity] || SEVERITY_COLOR.medium}`}>
                {inc.severity}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLOR[inc.status] || STATUS_COLOR.open}`}>
                {inc.status}
              </span>
              <span className="rounded-full bg-[#fafcfa] px-2 py-0.5 text-[10px] font-bold text-[#5c665a]">×{inc.count}</span>
            </div>
            <h2 className="mt-1 font-mono text-lg font-extrabold text-[#1a1c19]" data-testid="incident-drawer-error">{inc.error_type}</h2>
            <p className="mt-0.5 text-xs text-[#5c665a]">{inc.method} {inc.path || "—"} · {inc.source} · first seen {inc.first_seen?.slice(0, 16).replace("T", " ")}</p>
          </div>
          <button onClick={onClose} className="rounded-full bg-[#fafcfa] px-3 py-1 text-xs font-bold" data-testid="incident-drawer-close">Close</button>
        </div>
        <div className="mt-4 rounded-lg bg-[#fafcfa] p-3 text-xs text-[#1a1c19]" data-testid="incident-drawer-message">
          {inc.message}
        </div>

        <div className="mt-5 rounded-lg border border-[#e2e8e2] p-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">AI analysis</div>
            <button className="admin-btn" onClick={() => onAnalyze(inc.id)} disabled={analyzing} data-testid="incident-drawer-analyze">
              {analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {analyzing ? "Working…" : inc.ai ? "Re-run" : "Run analysis"}
            </button>
          </div>
          {inc.ai ? (
            <div className="mt-2 space-y-2 text-sm" data-testid="incident-drawer-ai">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">Root cause</div>
                <div className="text-[#1a1c19]">{inc.ai.root_cause || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">Suggested fix</div>
                <pre className="whitespace-pre-wrap font-mono text-xs text-[#1a1c19]">{inc.ai.suggested_fix || "—"}</pre>
              </div>
              <div className="text-[10px] text-[#5c665a]">
                Confidence: <strong>{inc.ai.confidence}</strong> · Model: <code>{inc.ai.model}</code> · {inc.ai.at?.slice(0, 16).replace("T", " ")}
              </div>
            </div>
          ) : (
            <p className="mt-2 text-xs text-[#5c665a]">No analysis yet. Click <strong>Run analysis</strong> to have Claude produce a root-cause + suggested fix.</p>
          )}
        </div>

        <div className="mt-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">Stack (truncated)</div>
          <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-[#0f1612] p-3 font-mono text-[11px] text-[#a8e5ff]" data-testid="incident-drawer-stack">
            {(inc.sample?.stack) || inc.stack_top || "(no stack captured)"}
          </pre>
        </div>
        {inc.sample?.user_agent && (
          <div className="mt-3 text-[10px] text-[#5c665a]">
            User agent: <code>{inc.sample.user_agent}</code>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          {inc.status !== "ack" && (
            <button className="admin-btn-ghost" onClick={() => onStatus(inc.id, "ack")} data-testid="incident-ack">
              Acknowledge
            </button>
          )}
          {inc.status !== "resolved" && (
            <button className="admin-btn" onClick={() => onStatus(inc.id, "resolved")} data-testid="incident-resolve">
              <CheckCircle2 className="h-4 w-4" /> Mark resolved
            </button>
          )}
          {inc.status !== "open" && (
            <button className="admin-btn-ghost" onClick={() => onStatus(inc.id, "open")} data-testid="incident-reopen">
              Reopen
            </button>
          )}
          <button className="admin-btn-ghost text-[#c01974]" onClick={() => onDelete(inc.id)} data-testid="incident-delete">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
